import { Key, ReactNode } from "react";

import Table, { TableProps } from "@mui/material/Table";
import TableBody, { TableBodyProps } from "@mui/material/TableBody";
import TableCell, { TableCellProps } from "@mui/material/TableCell";
import TableRow, { TableRowProps } from "@mui/material/TableRow";
import Typography, { TypographyProps } from "@mui/material/Typography";
import { SxProps, Theme } from "@mui/material/styles";

const DEFAULT_BODY_SPACING_SX: SxProps<Theme> = {
  "& > tr + tr": {
    "& > td, & > th": {
      paddingTop: "0.5em",
    },
  },
};

const DEFAULT_LABEL_CELL_SX: SxProps<Theme> = {
  p: 0,
  border: 0,
  width: "100px",
};

const DEFAULT_VALUE_CELL_SX: SxProps<Theme> = {
  p: 0,
  border: 0,
};

type OptionalSx = SxProps<Theme> | undefined | null | false;

const mergeSx = (...styles: OptionalSx[]): SxProps<Theme> => {
  const mergedStyles: SxProps<Theme>[] = [];

  styles.forEach(style => {
    if (!style) {
      return;
    }

    if (Array.isArray(style)) {
      mergedStyles.push(...style);

      return;
    }

    mergedStyles.push(style);
  });

  return mergedStyles as SxProps<Theme>;
};

export interface DetailsTableItem {
  key?: Key;
  label: ReactNode;
  content: ReactNode;
  rowProps?: TableRowProps;
  labelCellProps?: TableCellProps;
  contentCellProps?: TableCellProps;
}

export interface DetailsTableProps {
  items: DetailsTableItem[];
  tableProps?: TableProps;
  tableBodyProps?: TableBodyProps;
  labelTypographyProps?: TypographyProps;
}

const DetailsTable = ({
  items,
  tableProps,
  tableBodyProps,
  labelTypographyProps,
}: DetailsTableProps): JSX.Element => {
  const { sx: tableBodySx, ...restTableBodyProps } = tableBodyProps ?? {};

  return (
    <Table size="small" {...tableProps}>
      <TableBody
        sx={mergeSx(DEFAULT_BODY_SPACING_SX, tableBodySx)}
        {...restTableBodyProps}
      >
        {items.map(
          (
            { key, label, content, rowProps, labelCellProps, contentCellProps },
            index
          ) => {
            const {
              sx: labelCellSx,
              variant: labelCellVariant,
              ...restLabelCellProps
            } = labelCellProps ?? {};

            const { sx: contentCellSx, ...restContentCellProps } =
              contentCellProps ?? {};

            const typographyProps: TypographyProps = {
              variant: "body2Regular",
              color: "text.secondary",
              component: "span",
              ...labelTypographyProps,
            };

            return (
              <TableRow key={key ?? index} {...rowProps}>
                <TableCell
                  variant={labelCellVariant ?? "head"}
                  sx={mergeSx(DEFAULT_LABEL_CELL_SX, labelCellSx)}
                  {...restLabelCellProps}
                >
                  <Typography {...typographyProps}>{label}</Typography>
                </TableCell>

                <TableCell
                  sx={mergeSx(DEFAULT_VALUE_CELL_SX, contentCellSx)}
                  {...restContentCellProps}
                >
                  {content}
                </TableCell>
              </TableRow>
            );
          }
        )}
      </TableBody>
    </Table>
  );
};

export default DetailsTable;
